<template>
    <div class="gmf-social-board-cards-container">
        <div class="gmf-subheader">
            {{ t('social_feed.board_list.title') }}
        </div>
        <div 
            class="gmf-social-board-cards" 
            ref="cardsContainer"
            @mousedown.prevent="startDrag"
            @mousemove.prevent="drag"
            @mouseup.prevent="endDrag"
            @mouseleave.prevent="endDrag"
        >
            <button class="gmf-social-board-card" v-for="board in boards" :key="board.id" @click="selectBoard(board)">
                <div class="gmf-social-board-card-image" :style="{ backgroundImage: `url(${board.imageUrl})` }">
                    <template v-if="followBoardsEnabled">
                        <button class="gmf-social-board-card-follow-btn gmf-btn gmf-btn-sm gmf-btn-primary" v-if="!followedBoardsMap[board.id]" @click.stop.prevent="followBoard($event, board)">
                            <Icon icon="follow" /> {{ t('follow') }}
                        </button>
                        <button class="gmf-social-board-card-follow-btn gmf-btn gmf-btn-sm gmf-btn-secondary" v-else @click.stop.prevent="unfollowBoard($event, board)">
                            <Icon icon="following" /> {{ t('following') }}
                        </button>
                    </template>
                </div>
                <div class="gmf-social-board-card-title">
                    {{ board.name }}
                </div>
            </button>
        </div>
    </div>
</template>

<style>
.gmf-social-board-cards-container {
    margin-top: var(--gmf-p-4);
    margin-bottom: var(--gmf-p-5);
}
.gmf-modal-body .gmf-social-board-cards-container {
    margin-left: var(--gmf-p-4);
    margin-right: var(--gmf-p-4);
}

.gmf-social-board-cards {
    display: flex;
    flex-direction: row;
    gap: var(--gmf-p-4);
    margin-top: var(--gmf-p-3);

    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: auto;
    cursor: grab;
    user-select: none;
}

.gmf-social-board-cards.dragging {
    cursor: grabbing;
    scroll-behavior: auto;
}

.gmf-social-board-card {
    /* Remove default button styles */
    border: none;
    padding: 0;
    margin: 0;
    background: none;
    cursor: pointer;

    /* Add custom styles */
    background: var(--gmf-brand-color);
    width: 200px !important;
    border-radius: var(--gmf-rounding);
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;
    overflow: hidden;
    flex-shrink: 0;
}

/* smaller cards on mobile */
@media (max-width: 768px) {
    .gmf-social-board-card {
        width: 150px !important;
    }
}

.gmf-social-board-card-image {
    aspect-ratio: 4/3;
    width: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
}

.gmf-social-board-card .gmf-social-board-card-title {
    color: var(--gmf-col-white);
    font-weight: bold;
    padding: var(--gmf-p-3) var(--gmf-p-4);

    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.gmf-social-board-card .gmf-social-board-card-follow-btn {
    position: absolute;
    top: var(--gmf-p-3);
    right: var(--gmf-p-3);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--gmf-p-2);
    line-height: 0;
}
.gmf-social-board-card .gmf-social-board-card-follow-btn .gmf-icon {
    width: 16px !important;
    height: 16px !important;
    flex-shrink: 0;
}
</style>

<script>
import state from '@/state';
import callApi from '@/utils/callApi';
import tooltip from '@/utils/tooltip';
import Icon from '@/components/Icon.vue';

export default {
    components: {
        Icon,
    },
    emits: ['selectBoard'],
    props: {
        boards: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            followedBoardsMap: {},
            isDragging: false,
            startX: 0,
            scrollLeft: 0,
            hasDragged: false,
            dragThreshold: 5,
        };
    },
    mounted() {
        this.listFollows();
    },
    computed: {
        followBoardsEnabled() {
            return state.merchantConfig.follow?.boards?.enabled;
        },
        profile() {
            return state.profile;
        },
    },
    methods: {
        t: state.t,
        selectBoard(board) {
            if (!this.hasDragged) {
                this.$emit('selectBoard', board.id);
            }
        },
        async listFollows() {
            const response = await callApi('GET', '/v3/follow/social_board').then(res => res.json());
            for (const { id } of response.following) {
                this.followedBoardsMap[id] = true;
            }
        },
        async followBoard(ev, board) {
            if (!this.profile) {
                tooltip.create({x: ev.pageX, y: ev.pageY}, this.t('error_text.log_in_to_follow'));
                return;
            }
            
            const response = await callApi('POST', `/v3/follow/social_board/${board.id}`);
            if (response.status === 200) {
                this.followedBoardsMap[board.id] = true;
            }
        },
        async unfollowBoard(ev, board) {
            if (!this.profile) {
                tooltip.create({x: ev.pageX, y: ev.pageY}, this.t('error_text.log_in_to_follow'));
                return;
            }
            
            const response = await callApi('DELETE', `/v3/follow/social_board/${board.id}`);
            if (response.status === 200) {
                delete this.followedBoardsMap[board.id];
            }
        },
        startDrag(e) {
            if (e.target.closest('.gmf-social-board-card-follow-btn')) {
                return;
            }
            this.isDragging = true;
            this.hasDragged = false;
            this.startX = e.pageX - this.$refs.cardsContainer.offsetLeft;
            this.scrollLeft = this.$refs.cardsContainer.scrollLeft;
            this.$refs.cardsContainer.classList.add('dragging');
        },
        drag(e) {
            if (!this.isDragging) return;
            e.preventDefault();
            const x = e.pageX - this.$refs.cardsContainer.offsetLeft;
            const walk = x - this.startX;
            
            if (Math.abs(walk) > this.dragThreshold) {
                this.hasDragged = true;
            }
            
            this.$refs.cardsContainer.scrollLeft = this.scrollLeft - walk;
        },
        endDrag() {
            this.isDragging = false;
            this.$refs.cardsContainer.classList.remove('dragging');
        },
    },
};
</script>